import apiClient from '../../../services/apiClient';

import {
  unwrapData,
  unwrapList,
} from './settingsResponse';

const getRoleBasePath = (
  schoolId,
) =>
  `/schools/${schoolId}/rbac/roles`;

const getUserRoleBasePath = (
  schoolId,
  userId,
) =>
  `/schools/${schoolId}/rbac/users/${userId}/roles`;

const normalizeRoleIds = (
  roleIds,
) =>
  [
    ...new Set(
      (Array.isArray(roleIds)
        ? roleIds
        : [roleIds]
      ).filter(Boolean),
    ),
  ];

export const rbacSettingsApi = {
  listRoles: async (schoolId) => {
    const response =
      await apiClient.get(
        `${getRoleBasePath(
          schoolId,
        )}/`,
      );

    return unwrapList(
      response.data,
    );
  },

  getRole: async (
    schoolId,
    roleId,
  ) => {
    const response =
      await apiClient.get(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/`,
      );

    return unwrapData(
      response.data,
    );
  },

  createRole: async (
    schoolId,
    payload,
  ) => {
    const response =
      await apiClient.post(
        `${getRoleBasePath(
          schoolId,
        )}/create/`,
        payload,
      );

    return unwrapData(
      response.data,
    );
  },

  updateRole: async (
    schoolId,
    roleId,
    payload,
  ) => {
    const response =
      await apiClient.patch(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/`,
        payload,
      );

    return unwrapData(
      response.data,
    );
  },

  deleteRole: async (
    schoolId,
    roleId,
  ) => {
    const response =
      await apiClient.delete(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/`,
      );

    return response.data;
  },

  listPermissions: async (
    schoolId,
  ) => {
    const response =
      await apiClient.get(
        `/schools/${schoolId}/rbac/permissions/`,
      );

    return unwrapList(
      response.data,
    );
  },

  listRolePermissions: async (
    schoolId,
    roleId,
  ) => {
    const response =
      await apiClient.get(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/permissions/list/`,
      );

    return unwrapList(
      response.data,
    );
  },

  addRolePermissions: async (
    schoolId,
    roleId,
    permissionIds,
  ) => {
    const response =
      await apiClient.post(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/permissions/`,
        {
          permission_ids:
            permissionIds,
        },
      );

    return response.data;
  },

  replaceRolePermissions: async (
    schoolId,
    roleId,
    permissionIds,
  ) => {
    const response =
      await apiClient.patch(
        `${getRoleBasePath(
          schoolId,
        )}/${roleId}/permissions/replace/`,
        {
          permission_ids:
            permissionIds,
        },
      );

    return response.data;
  },

  listUserRoles: async (
    schoolId,
    userId,
  ) => {
    const response =
      await apiClient.get(
        `${getUserRoleBasePath(
          schoolId,
          userId,
        )}/list/`,
      );

    return unwrapList(
      response.data,
    );
  },

  assignUserRoles: async (
    schoolId,
    userId,
    roleIds,
  ) => {
    const normalizedRoleIds =
      normalizeRoleIds(roleIds);

    if (
      normalizedRoleIds.length === 0
    ) {
      throw new Error(
        'Select at least one role.',
      );
    }

    const response =
      await apiClient.post(
        `${getUserRoleBasePath(
          schoolId,
          userId,
        )}/`,
        {
          role_ids:
            normalizedRoleIds,
        },
      );

    return response.data;
  },

  assignUserRole: async (
    schoolId,
    userId,
    roleId,
  ) =>
    rbacSettingsApi.assignUserRoles(
      schoolId,
      userId,
      [roleId],
    ),

  removeUserRole: async (
    schoolId,
    userId,
    roleId,
  ) => {
    const response =
      await apiClient.delete(
        `${getUserRoleBasePath(
          schoolId,
          userId,
        )}/${roleId}/`,
      );

    return response.data;
  },
};

export default rbacSettingsApi;